import {
  ORBI_RETURN_TYPE,
  isAppointmentReturnType,
  isBillingReturnType,
  isReturnType,
} from '@patient-ui/shared/constants';
import { OrbiMessageType } from '@patient-ui/shared/enums';
import {
  OrbiAccession,
  OrbiMessage,
  OrbiMessageRoute,
  OrbiStructuredData,
  OrbiTestCode,
} from '@patient-ui/shared/models';

import {
  orbiMessageLocksChat,
  resolveOrbiGuardrail,
} from './orbi-guardrail.util';

/**
 * Orbi response parser + render-path selector (PCSA-13164 → extended by
 * PCSA-13223) — turns a raw `/orbi-agent` 200 body into a routed [OrbiMessage]
 * the chat UI can render. Pure + TOTAL: it never throws; any malformed/
 * unexpected payload degrades safely (to text, or `fallback` when there is no
 * renderable content).
 *
 * Render-path selection (PCSA-13223 AC), decided from `guardrailResult` +
 * `returnType` only — never from the wording of `textualResponse`:
 *  - LOCKING guardrail (EMERGENCY/HARM/…)      → `guardrail`
 *  - non-locking guardrail (OFFTOPIC/FOREIGN…) → `text` (still carries the data)
 *  - `PatientResults`                          → `results-card`
 *  - Appointment* / VisitHistory return types  → `appointment-card`
 *  - Billing action return types               → `billing-card`
 *  - explicit `Text` / unknown / absent + text → `text`
 *  - no renderable content                     → `fallback`
 *
 * Mirrors the mobile parser in `orbi_chat_service.dart` for casing tolerance,
 * lenient `data` decoding, the single `agent === 'Results'` inference, and
 * guardrail resolution, WITHOUT branching on Clinical/Data/Support agent labels.
 * NOTE: distinct render-path strings + the billing card are web-specific
 * (per the 13223 AC); mobile carries typed payloads and decides at the render
 * site and has no billing card — a justified, documented divergence. Rendering
 * of each path is a separate story (text 13183 / results 13224 / appointment
 * 13241 / guardrail 13242 / fallback 13172). The PatientResults payload is
 * mapped into typed `accessions` (PCSA-13224, mirroring mobile `_parseAccessions`
 * — `header` + `orderedItems[]`); the Results card renders from those, not FHIR.
 */

/** Reads the first present key (camelCase or PascalCase) as a string. */
function pickString(
  obj: Record<string, unknown>,
  ...keys: string[]
): string | undefined {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === 'string') {
      return value;
    }
  }
  return undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function tryJsonParse(source: string): unknown {
  try {
    return JSON.parse(source);
  } catch {
    return null;
  }
}

/**
 * Decodes a single `data` element into a list of maps. Handles a direct object,
 * a JSON string encoding an object or array, and the gateway's malformed
 * `"{...}]"` shape (object with a trailing `]` but no leading `[`) by retrying
 * with a prepended `[`. Mirrors mobile `_decodeDataItem`.
 */
export function decodeOrbiDataItem(item: unknown): Record<string, unknown>[] {
  if (isRecord(item)) {
    return [item];
  }

  if (typeof item === 'string') {
    const decoded = tryJsonParse(item);
    if (isRecord(decoded)) {
      return [decoded];
    }
    if (Array.isArray(decoded)) {
      return decoded.filter(isRecord);
    }
    // The API sometimes returns "{...}]" (object with a trailing `]` but no
    // leading `[`). Prepending `[` yields valid JSON: "[{...}]".
    if (decoded === null && item.startsWith('{')) {
      const retried = tryJsonParse(`[${item}`);
      if (Array.isArray(retried)) {
        return retried.filter(isRecord);
      }
    }
  }

  return [];
}

/**
 * Flattens a `data` value (expected to be an array of elements, each decoded via
 * `decodeOrbiDataItem`) into a single list of maps. Tolerates a non-array `data`
 * (a bare object or JSON string) by decoding it directly. Shared by the
 * results / appointment / billing card branches (PCSA-13223) so each card path
 * receives the same leniently decoded payload shape.
 */
export function decodeOrbiDataList(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) {
    return data.reduce<Record<string, unknown>[]>(
      (acc, item) => acc.concat(decodeOrbiDataItem(item)),
      []
    );
  }
  return decodeOrbiDataItem(data);
}

/** Reads a numeric field (tolerating a numeric string) or returns undefined. */
function pickNumber(
  obj: Record<string, unknown>,
  key: string
): number | undefined {
  const value = obj[key];
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

/** Reads a boolean field, or undefined when absent/non-boolean. */
function pickBoolean(
  obj: Record<string, unknown>,
  key: string
): boolean | undefined {
  return typeof obj[key] === 'boolean' ? (obj[key] as boolean) : undefined;
}

/**
 * Maps a decoded PatientResults `data[]` into typed [OrbiAccession] cards
 * (PCSA-13224). Mirrors mobile `orbi_chat_service._parseAccessions`: each
 * decoded element carries a `{ header, orderedItems }` shape; `header.id` is
 * required (elements without it are skipped), `header` fields map to the
 * accession, and `orderedItems[].{ testName, testCode }` become the test list.
 * Casing-tolerant on `header` (camelCase or PascalCase) to match the rest of
 * the parser. Returns `undefined` when nothing maps, so the message carries no
 * `accessions` and the card degrades to the text/fallback path.
 */
function parseOrbiAccessions(
  decoded: Record<string, unknown>[]
): OrbiAccession[] | undefined {
  const accessions: OrbiAccession[] = [];

  for (const entry of decoded) {
    const header = (entry['header'] ?? entry['Header']) as unknown;
    if (!isRecord(header)) {
      continue;
    }
    const id = pickNumber(header, 'id') ?? pickNumber(header, 'Id');
    if (id === undefined) {
      continue;
    }

    const orderedItems = (entry['orderedItems'] ??
      entry['OrderedItems']) as unknown;
    const testCodes: OrbiTestCode[] = [];
    if (Array.isArray(orderedItems)) {
      for (const item of orderedItems) {
        if (!isRecord(item)) {
          continue;
        }
        const testName = pickString(item, 'testName', 'TestName');
        const testCode = pickString(item, 'testCode', 'TestCode');
        if (testName && testCode) {
          testCodes.push({ testCode, testName });
        }
      }
    }

    accessions.push({
      id,
      dateOfService: pickString(header, 'dateOfService', 'DateOfService'),
      orderingProviderName: pickString(
        header,
        'orderingProviderName',
        'OrderingProviderName'
      ),
      accountName: pickString(header, 'accountName', 'AccountName'),
      dashboardCategory: pickString(
        header,
        'dashboardCategory',
        'DashboardCategory'
      ),
      orderedDate: pickString(header, 'orderedDate', 'OrderedDate'),
      reportDate: pickString(header, 'reportDate', 'ReportDate'),
      patientName: pickString(header, 'patientName', 'PatientName'),
      pid: pickNumber(header, 'pid') ?? pickNumber(header, 'Pid'),
      isDetailAvailable:
        pickBoolean(header, 'isDetailAvailable') ??
        pickBoolean(header, 'IsDetailAvailable'),
      isNew: pickBoolean(header, 'isNew') ?? pickBoolean(header, 'IsNew'),
      isUpdated:
        pickBoolean(header, 'isUpdated') ?? pickBoolean(header, 'IsUpdated'),
      isUpcoming:
        pickBoolean(header, 'isUpcoming') ?? pickBoolean(header, 'IsUpcoming'),
      testCodes: testCodes.length > 0 ? testCodes : undefined,
    });
  }

  return accessions.length > 0 ? accessions : undefined;
}

/**
 * Extracts the billing `data.invoices` list — the invoice NUMBERS the billing
 * card / flow needs. The agent's contract
 * (`BillingAgentResponseModel.BillingDataModel`) is
 * `{"invoices": ["<invoiceNumber>", ...]}` — identifiers only, never invoice
 * objects: the UI fetches its own invoice detail from these numbers. `data` is
 * an OBJECT here (not an array), so it arrives decoded as a single-element list.
 * Numeric elements are coerced to strings (JSON may carry either) and blanks are
 * dropped. Returns `undefined` when no numbers are present — `InvoiceHistory`
 * legitimately carries no `data`, and `FindABill` may carry `{"invoices": []}`,
 * so an empty list must not be confused with "the agent sent numbers".
 */
function parseOrbiInvoiceNumbers(
  decoded: Record<string, unknown>[]
): string[] | undefined {
  const invoiceNumbers: string[] = [];

  for (const entry of decoded) {
    const list = entry['invoices'] ?? entry['Invoices'];
    if (!Array.isArray(list)) {
      continue;
    }
    for (const item of list) {
      const value =
        typeof item === 'string'
          ? item.trim()
          : typeof item === 'number' && Number.isFinite(item)
          ? String(item)
          : '';
      if (value) {
        invoiceNumbers.push(value);
      }
    }
  }

  return invoiceNumbers.length > 0 ? invoiceNumbers : undefined;
}

/**
 * Normalizes an appointment-collection payload (`AppointmentDetails` /
 * `VisitHistory`) into a flat list of appointment maps. Mirrors mobile
 * (`orbi_chat_bubble.dart`): an `appointments` key carries the MULTI payload,
 * and its absence means the decoded map IS a single appointment. The service
 * already coerces a bare array and the MCP `{content:[{text}]}` envelope into
 * `{"appointments":[...]}` (`NormalizeAppointmentData`), so this only has to
 * handle the normalized shape plus the single-map case. Returns `undefined` when
 * nothing maps, so the card can degrade instead of rendering empty.
 */
function parseOrbiAppointments(
  decoded: Record<string, unknown>[]
): Record<string, unknown>[] | undefined {
  const appointments: Record<string, unknown>[] = [];

  for (const entry of decoded) {
    const embedded = entry['appointments'] ?? entry['Appointments'];
    if (Array.isArray(embedded)) {
      appointments.push(...embedded.filter(isRecord));
    } else if (Object.keys(entry).length > 0) {
      appointments.push(entry);
    }
  }

  return appointments.length > 0 ? appointments : undefined;
}

/**
 * Parses + routes a raw agent response into an [OrbiMessage]. Never throws.
 */
export function parseOrbiAgentResponse(raw: unknown): OrbiMessage {
  const body: Record<string, unknown> = isRecord(raw) ? raw : {};

  const text = pickString(body, 'textualResponse', 'TextualResponse') ?? '';
  const agent = pickString(body, 'agent', 'Agent') ?? '';
  const messageId = pickString(body, 'messageId', 'MessageId');
  let returnType = pickString(body, 'returnType', 'ReturnType');
  const data = (body['data'] ?? body['Data']) as unknown;

  // The ONLY agent-label inference (mirrors mobile): a Results answer with a
  // list payload but no explicit returnType is a PatientResults card. We do NOT
  // branch on Clinical / Data / Support. Casing-tolerant (`results`/`RESULTS`),
  // consistent with `pickString` and `resolveOrbiGuardrail` below — the gateway's
  // casing for `agent` isn't pinned yet (live 200 uncaptured).
  if (
    returnType == null &&
    agent.toLowerCase() === 'results' &&
    Array.isArray(data)
  ) {
    returnType = ORBI_RETURN_TYPE.PatientResults;
  }

  // Guardrail indicator (data only — lock UX is PCSA-13170).
  const guardrail = resolveOrbiGuardrail(body, agent, returnType);

  const base = {
    text,
    type: OrbiMessageType.Llm,
    agent: agent || undefined,
    messageId,
    returnType,
    guardrailTriggered: guardrail.triggered,
    guardrailType: guardrail.type,
  };

  // Routing (PCSA-13223). Decided from guardrailResult + returnType ONLY — never
  // the wording of `textualResponse`. Wrapped so a structured-branch failure
  // degrades safely rather than throwing.
  try {
    // AC 4: a LOCKING guardrail (EMERGENCY/HARM/…) routes to the guardrail path;
    // a non-locking (soft: OFFTOPIC/FOREIGNLANGUAGE) guardrail does NOT — it
    // falls through to the text path (still carrying the guardrail data).
    if (orbiMessageLocksChat(guardrail.triggered, guardrail.type)) {
      return { ...base, route: 'guardrail' as OrbiMessageRoute };
    }

    // AC 5a: Results card. Map the decoded PatientResults payload into typed
    // accessions the card renders (PCSA-13224, mirrors mobile). If nothing maps
    // (e.g. a status-only / empty payload), fall through so the turn degrades to
    // its text/fallback path rather than showing an empty card.
    if (
      isReturnType(returnType, ORBI_RETURN_TYPE.PatientResults) &&
      Array.isArray(data)
    ) {
      const decoded = decodeOrbiDataList(data);
      const accessions = parseOrbiAccessions(decoded);
      if (accessions) {
        const structuredData: OrbiStructuredData = {
          // Non-null by the guard above, which `isReturnType` cannot narrow the
          // way the previous `===` did — the same cast the two sibling branches
          // below already use.
          returnType: returnType as string,
          raw: decoded,
          accessions,
        };
        return {
          ...base,
          route: 'results-card' as OrbiMessageRoute,
          structuredData,
        };
      }
    }

    // AC 5b: Appointments card (AppointmentDetails / VisitHistory / PatientPass /
    // FindLab / AppointmentCancelled). The collection types additionally carry a
    // flattened `appointments` list; the single-map types (PatientPass, FindLab,
    // Cancelled) are handed to the card as-is via `raw`, since each has its own
    // typed shape the renderer reads directly.
    if (isAppointmentReturnType(returnType)) {
      const decoded = decodeOrbiDataList(data);
      // Matched the same tolerant way as the predicate that got us here: a
      // spaced or differently-cased `Visit History` routes to this card, so
      // deciding "is it a collection" with `===` would hand the renderer a card
      // with no `appointments` and draw it empty.
      const isCollection =
        isReturnType(returnType, ORBI_RETURN_TYPE.AppointmentDetails) ||
        isReturnType(returnType, ORBI_RETURN_TYPE.VisitHistory);
      const structuredData: OrbiStructuredData = {
        returnType: returnType as string,
        raw: decoded,
        appointments: isCollection ? parseOrbiAppointments(decoded) : undefined,
      };
      return {
        ...base,
        route: 'appointment-card' as OrbiMessageRoute,
        structuredData,
      };
    }

    // AC 5c: Billing card / flow (PayABill, PaymentPlan, Invoices,
    // InvoiceHistory, UpdateInsurance, FindABill). `data.invoices` carries the
    // invoice NUMBERS the flow needs; it is legitimately absent on
    // `InvoiceHistory` and may be empty on `FindABill`.
    if (isBillingReturnType(returnType)) {
      const decoded = decodeOrbiDataList(data);
      const structuredData: OrbiStructuredData = {
        returnType: returnType as string,
        raw: decoded,
        invoiceNumbers: parseOrbiInvoiceNumbers(decoded),
      };
      return {
        ...base,
        route: 'billing-card' as OrbiMessageRoute,
        structuredData,
      };
    }

    // AC 6/9: the agents' explicit `Text` contract, or an unknown/absent
    // returnType. If there is text to show, route to text — the default for
    // Clinical/Support answers, for an explicit `Text` return (deliberately NOT
    // a member of the appointment/billing predicates, so it lands here), and for
    // any unrecognized returnType (no unhandled error). Otherwise there is no
    // renderable content (AC 7) → fallback.
    if (text.trim().length > 0) {
      return { ...base, route: 'text' as OrbiMessageRoute };
    }
    return { ...base, route: 'fallback' as OrbiMessageRoute };
  } catch {
    // Total guarantee (AC 7/9): a structured-branch failure never throws. Degrade
    // to text when there is content to show, else to fallback.
    return {
      text,
      type: OrbiMessageType.Llm,
      route: (text.trim().length > 0 ? 'text' : 'fallback') as OrbiMessageRoute,
      agent: agent || undefined,
      messageId,
      returnType,
      guardrailTriggered: guardrail.triggered,
      guardrailType: guardrail.type,
    };
  }
}
